import { IProductVariant, IProductVariantMedia } from '@/lib/types/core/product-variant'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface ProductVariantsState {
  variants: IProductVariant[]
  selectedVariant: IProductVariant | null
  loading: boolean
  error: string | null
  mediaUploading: boolean
  mediaError: string | null
}

const initialState: ProductVariantsState = {
  variants: [],
  selectedVariant: null,
  loading: false,
  error: null,
  mediaUploading: false,
  mediaError: null
}

const productVariantsSlice = createSlice({
  name: 'productVariants',
  initialState,
  reducers: {
    setVariants: (state, action: PayloadAction<IProductVariant[]>) => {
      state.variants = action.payload
    },
    addVariant: (state, action: PayloadAction<IProductVariant>) => {
      state.variants.push(action.payload)
    },
    updateVariant: (state, action: PayloadAction<IProductVariant>) => {
      const index = state.variants.findIndex((v) => v.id === action.payload.id)
      if (index !== -1) {
        state.variants[index] = action.payload
      }
    },
    removeVariant: (state, action: PayloadAction<string>) => {
      state.variants = state.variants.filter((v) => v.id !== action.payload)
    },
    setSelectedVariant: (state, action: PayloadAction<IProductVariant | null>) => {
      state.selectedVariant = action.payload
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    setMediaUploading: (state, action: PayloadAction<boolean>) => {
      state.mediaUploading = action.payload
    },
    setMediaError: (state, action: PayloadAction<string | null>) => {
      state.mediaError = action.payload
    },
    updateVariantMedia: (state, action: PayloadAction<{ variantId: string; media: IProductVariantMedia }>) => {
      const variant = state.variants.find((v) => v.id === action.payload.variantId)
      if (variant) {
        variant.media = action.payload.media
        variant.mediaId = action.payload.media.id
      }
      if (state.selectedVariant?.id === action.payload.variantId) {
        state.selectedVariant.media = action.payload.media
        state.selectedVariant.mediaId = action.payload.media.id
      }
    },
    removeVariantMedia: (state, action: PayloadAction<string>) => {
      const variant = state.variants.find((v) => v.id === action.payload)
      if (variant) {
        variant.media = null
        variant.mediaId = null
      }
      if (state.selectedVariant?.id === action.payload) {
        state.selectedVariant.media = null
        state.selectedVariant.mediaId = null
      }
    }
  }
})

export const {
  setVariants,
  addVariant,
  updateVariant,
  removeVariant,
  setSelectedVariant,
  setLoading,
  setError,
  setMediaUploading,
  setMediaError,
  updateVariantMedia,
  removeVariantMedia,
  // Removed undefined actions
} = productVariantsSlice.actions

export default productVariantsSlice.reducer
